import api from "./api";

const extractData = (res) => res?.data?.data ?? res?.data ?? null;

const marketplaceCartService = {
  getCart: () => api.get("/hub-home/cart"),
  getCount: () => api.get("/hub-home/cart/count"),
  getProductIds: () => api.get("/hub-home/cart/product-ids"),
  addItem: (productId, quantity = 1) =>
    api.post("/hub-home/cart/items", { productId, quantity }),
  updateItem: (cartItemId, payload) =>
    api.patch(`/hub-home/cart/items/${cartItemId}`, payload),
  updateSelection: (payload) => api.patch("/hub-home/cart/selection", payload),
  removeItem: (cartItemId) => api.delete(`/hub-home/cart/items/${cartItemId}`),
  removeSelected: () => api.delete("/hub-home/cart/selected"),
  moveToWishlist: (payload = {}) => api.post("/hub-home/cart/move-to-wishlist", payload),
  saveAsList: (payload = {}) => api.post("/hub-home/cart/save-as-list", payload),
  generateRfq: (payload = {}) => api.post("/hub-home/cart/generate-rfq", payload),
  placeCheckout: (payload = {}) => api.post("/hub-home/cart/checkout/place-order", payload),
  getWishlist: () => api.get("/hub-home/wishlist"),
  addWishlistItem: (productId) => api.post("/hub-home/wishlist", { productId }),
  removeWishlistItem: (productId) => api.delete(`/hub-home/wishlist/${productId}`),
  getSavedLists: () => api.get("/hub-home/saved-lists"),
  createSavedList: (payload = {}) => api.post("/hub-home/saved-lists", payload),
  getSavedList: (listId) => api.get(`/hub-home/saved-lists/${listId}`),
  renameSavedList: (listId, payload = {}) => api.patch(`/hub-home/saved-lists/${listId}`, payload),
  updateSavedListItem: (listId, productId, payload = {}) =>
    api.patch(`/hub-home/saved-lists/${listId}/items/${productId}`, payload),
  moveSavedListItems: (listId, payload = {}) => api.post(`/hub-home/saved-lists/${listId}/move-items`, payload),
  deleteSavedList: (listId) => api.delete(`/hub-home/saved-lists/${listId}`),
  removeSavedListItem: (listId, productId) =>
    api.delete(`/hub-home/saved-lists/${listId}/items/${productId}`),
  addSavedListToCart: (listId, payload = {}) =>
    api.post(`/hub-home/saved-lists/${listId}/add-to-cart`, payload),
};

export function notifyMarketplaceCartUpdated() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("marketplace-cart-updated"));
  }
}

export async function fetchMarketplaceCart() {
  const res = await marketplaceCartService.getCart();
  return extractData(res);
}

export async function fetchMarketplaceCartCount() {
  const res = await marketplaceCartService.getCount();
  return Number(extractData(res)?.count) || 0;
}

export async function fetchMarketplaceCartProductIds() {
  const res = await marketplaceCartService.getProductIds();
  const data = extractData(res);
  const ids = data?.productIds ?? data?.product_ids ?? [];
  return Array.isArray(ids) ? ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0) : [];
}

export async function addMarketplaceCartItem(productId, quantity = 1) {
  const res = await marketplaceCartService.addItem(productId, quantity);
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function updateMarketplaceCartItem(cartItemId, payload) {
  const res = await marketplaceCartService.updateItem(cartItemId, payload);
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function updateMarketplaceCartSelection(payload) {
  const res = await marketplaceCartService.updateSelection(payload);
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function removeMarketplaceCartItem(cartItemId) {
  const res = await marketplaceCartService.removeItem(cartItemId);
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function removeSelectedMarketplaceCartItems() {
  const res = await marketplaceCartService.removeSelected();
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function moveMarketplaceCartToWishlist(payload = {}) {
  const res = await marketplaceCartService.moveToWishlist(payload);
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function saveMarketplaceCartAsList(listName, payload = {}) {
  const res = await marketplaceCartService.saveAsList({ listName, ...payload });
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function generateMarketplaceCartRfqs(payload = {}) {
  const res = await marketplaceCartService.generateRfq(payload);
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function placeMarketplaceCartCheckout(payload = {}) {
  const res = await marketplaceCartService.placeCheckout(payload);
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export async function fetchMarketplaceWishlist() {
  const res = await marketplaceCartService.getWishlist();
  return extractData(res);
}

export async function removeMarketplaceWishlistItem(productId) {
  const res = await marketplaceCartService.removeWishlistItem(productId);
  return extractData(res);
}

export async function addMarketplaceWishlistItem(productId) {
  const res = await marketplaceCartService.addWishlistItem(productId);
  return extractData(res);
}

export async function fetchMarketplaceSavedLists() {
  const res = await marketplaceCartService.getSavedLists();
  return extractData(res);
}

export async function createMarketplaceSavedList(listName) {
  const res = await marketplaceCartService.createSavedList({ listName });
  return extractData(res);
}

export async function renameMarketplaceSavedList(listId, listName) {
  const res = await marketplaceCartService.renameSavedList(listId, { listName });
  return extractData(res);
}

export async function updateMarketplaceSavedListItem(listId, productId, quantity) {
  const res = await marketplaceCartService.updateSavedListItem(listId, productId, { quantity });
  return extractData(res);
}

export async function moveMarketplaceSavedListItems(listId, targetListId, productIds) {
  const res = await marketplaceCartService.moveSavedListItems(listId, { targetListId, productIds });
  return extractData(res);
}

export async function fetchMarketplaceSavedListDetail(listId) {
  const res = await marketplaceCartService.getSavedList(listId);
  return extractData(res);
}

export async function deleteMarketplaceSavedList(listId) {
  const res = await marketplaceCartService.deleteSavedList(listId);
  return extractData(res);
}

export async function removeMarketplaceSavedListItem(listId, productId) {
  const res = await marketplaceCartService.removeSavedListItem(listId, productId);
  return extractData(res);
}

export async function addMarketplaceSavedListToCart(listId, productIds) {
  const payload = Array.isArray(productIds) && productIds.length ? { productIds } : {};
  const res = await marketplaceCartService.addSavedListToCart(listId, payload);
  const data = extractData(res);
  notifyMarketplaceCartUpdated();
  return data;
}

export default marketplaceCartService;
